import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

export default function Auth() {
  const { login, register } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const formatError = (detail) => {
    if (!detail) return lang === "de" ? "Ein Fehler ist aufgetreten." : "An error occurred.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(" ");
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.name) { setError(lang === "de" ? "Name ist erforderlich" : "Name is required"); setLoading(false); return; }
        await register(form.email, form.password, form.name);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm"
      >
        {/* Brand */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-2xl font-bold tracking-tighter text-foreground mb-1">
            LIRK<span className="text-foreground/30 font-light"> PRINT</span>
          </h1>
          <p className="text-xs font-mono tracking-widest text-foreground/30 uppercase">
            {mode === "login" ? t("auth.welcome") : t("auth.create_account")}
          </p>
        </div>

        {/* Toggle */}
        <div className="flex border border-border rounded-sm overflow-hidden mb-8">
          <button
            data-testid="login-tab"
            onClick={() => { setMode("login"); setError(""); }}
            className={`flex-1 py-3 text-xs font-mono tracking-widest uppercase transition-all ${mode === "login" ? "bg-foreground text-background" : "text-foreground/50 hover:text-foreground"}`}
          >
            {t("auth.login")}
          </button>
          <button
            data-testid="register-tab"
            onClick={() => { setMode("register"); setError(""); }}
            className={`flex-1 py-3 text-xs font-mono tracking-widest uppercase transition-all ${mode === "register" ? "bg-foreground text-background" : "text-foreground/50 hover:text-foreground"}`}
          >
            {t("auth.register")}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-xs font-mono tracking-wider text-foreground/40 uppercase mb-2">{t("auth.name")}</label>
              <input
                data-testid="auth-name-input"
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
                placeholder={lang === "de" ? "Dein Name" : "Your name"}
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-mono tracking-wider text-foreground/40 uppercase mb-2">{t("auth.email")}</label>
            <input
              data-testid="auth-email-input"
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
              placeholder={lang === "de" ? "deine@email.de" : "your@email.com"}
            />
          </div>
          <div>
            <label className="block text-xs font-mono tracking-wider text-foreground/40 uppercase mb-2">{t("auth.password")}</label>
            <input
              data-testid="auth-password-input"
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
              className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <motion.div
              data-testid="auth-error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm"
            >
              <p className="text-xs text-destructive font-mono">{error}</p>
            </motion.div>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-foreground text-background text-xs font-mono tracking-[0.2em] uppercase rounded-sm hover:bg-foreground/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
          >
            {loading
              ? (lang === "de" ? "Laden..." : "Loading...")
              : mode === "login" ? t("auth.login.submit") : t("auth.register.submit")
            }
          </button>
        </form>
      </motion.div>
    </div>
  );
}
